// validation.ts

export const BaseValidation: Record<string, RegExp> = {
  alphaNumeric: /^[a-zA-Z0-9]*$/,
  email: /^[^\s@]+@[^\s@]+\.[^\s@]+$/,
  url: /^(https?|ftp):\/\/[^\s/$.?#].[^\s]*$/,
  password: /^(?=.*\d)(?=.*[!@#$%^&*])(?=.*[a-z])(?=.*[A-Z]).{6,}$/,
  atleastOneAlphaOneNumeric: /^(?=.*[A-Za-z])(?=.*\d)[A-Za-z\d]+$/,
  alphabatic: /^[A-Za-z]+$/,
  nameAlphabatic: /^[A-Za-z ]+$/,
  numeric: /^[0-9]+$/,
  price: /^\d+(\.\d{1,2})?$/,
  username: /^[A-Za-z0-9 ]{2,20}$/,
  gst: /^[0-9]{2}[A-Z]{5}[0-9]{4}[A-Z]{1}[1-9A-Z]{1}Z[0-9A-Z]{1}$/,
  phone: /^\d{9}$/,
  vehicleNumber: /^[A-Z]{2}[0-9]{2}[A-Z]{1,2}[0-9]{4}$/,
  pinCode: /^[1-9][0-9]{5}$/,
  hexColor: /^#?[A-Fa-f0-9]{6}$/,
} as const;

// A generic type for a validation rule that has a value and a message
type Rule<T> = {
  value: T;
  message: string;
};

// Defines the structure for a set of validation rules for a single field
type FieldValidationRules = {
  pattern?: Rule<RegExp>;
  maxLength?: Rule<number>;
  minLength?: Rule<number>;
  validate?: (value: string) => boolean | string;
};
// Defines validation rules for phone objects (e.g., { phone: string })
type PhoneObjectValidationRules = {
  validate?: (value: { phone?: string }) => boolean | string;
};

// Defines the overall shape of the errorTypes object
type ErrorTypes = {
  required: (fieldName?: string) => { required: string };
  min: (fieldName?: string, value?: number) => { min: string };
  max: (fieldName?: string, value?: number) => { max: string };
  email: FieldValidationRules;
  phone: FieldValidationRules;
  phoneObject: PhoneObjectValidationRules;
  password: FieldValidationRules;
  name: FieldValidationRules;
  gstNumber: FieldValidationRules;
  pinCode: FieldValidationRules;
  aadhaar: FieldValidationRules;
  pan: FieldValidationRules;
  colorCode: FieldValidationRules
  // Add other validation types here as needed
};

export const errorTypes: ErrorTypes = {
  required: (fieldName = 'This field') => ({
    required: `${fieldName} is required`,
  }),

  min: (fieldName = 'This field', value = 0) => ({
    min: `${fieldName} must be at least ${value}`,
  }),

  max: (fieldName = 'This field', value = 0) => ({
    max: `${fieldName} must be at most ${value}`,
  }),

  email: {
    pattern: {
      value: BaseValidation.email,
      message: 'Invalid email address',
    },
    validate: (value) =>
      value === value?.toLowerCase() || 'Email must be in lowercase',
  },

  phone: {
    pattern: {
      value: BaseValidation.phone,
      message: 'Please enter a valid 9-digit mobile number',
    },
    maxLength: {
      value: 9,
      message: 'Mobile number cannot be more than 9 digits',
    },
    minLength: {
      value: 9,
      message: 'Mobile number must be exactly 9 digits',
    },
  },

  phoneObject: {
    validate: (value) => {
      // If phone is empty, it's valid (optional field)
      if (!value?.phone || value.phone.trim() === '') {
        return true;
      }
      // If phone is provided, validate it
      if (!BaseValidation.phone.test(value.phone)) {
        return 'Please enter a valid 9-digit mobile number';
      }
      if (value.phone.length !== 9) {
        return 'Mobile number must be exactly 9 digits';
      }
      return true;
    },
  },

  password: {
    minLength: {
      value: 6,
      message: 'Password must be at least 6 characters long',
    },
    pattern: {
      value: /^(?=.*[a-z])(?=.*[A-Z])(?=.*\d)(?=.*[\W_]).{6,}$/,
      message:
        'Password must include uppercase, lowercase, number, and special character',
    },
  },

  name: {
    pattern: {
      value: /^[a-zA-Z\s]*$/,
      message: 'Name must contain only letters and spaces',
    },
    maxLength: {
      value: 160,
      message: 'Name should not exceed 160 characters',
    },
  },

  gstNumber: {
    pattern: {
      value: BaseValidation?.gst,
      message:
        'Please enter a valid 15-digit GST number (e.g., 22ABCDE1234F1Z5)',
    },
    maxLength: {
      value: 15,
      message: 'GST number must be 15 characters',
    },
    minLength: {
      value: 15,
      message: 'GST number must be 15 characters',
    },
  },

  pinCode: {
    pattern: {
      value: BaseValidation.pinCode,
      message: 'Please enter a valid 6-digit PIN code',
    },
    maxLength: {
      value: 6,
      message: 'PIN code must be exactly 6 digits',
    },
    minLength: {
      value: 6,
      message: 'PIN code must be exactly 6 digits',
    },
  },

  aadhaar: {
    pattern: {
      value: /^[2-9]{1}[0-9]{11}$/, // Aadhaar must start from 2-9 and be 12 digits
      message:
        'Invalid Aadhaar Number (must be 12 digits, not starting with 0/1)',
    },
  },
  pan: {
    pattern: {
      value: /^[A-Z]{5}[0-9]{4}[A-Z]{1}$/, // PAN must follow AAAAA9999A format
      message: 'Invalid PAN format (e.g., ABCDE1234F)',
    },
  },

  colorCode: {
    pattern: {
      value: BaseValidation.hexColor,
      message: 'Please enter a valid 6-digit hex color (e.g. #A1B2C3)',
    },
    validate: (value) =>
      value.startsWith('#') || 'Color code must start with #',
  },
};
